/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useRef } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MapActionsContext, useSelectorWithMapId } from './BaseMapContainer';
import {
  selectMapRulerToolFollowAircraft,
  selectMapRulerToolMarkersAndPolylines,
  selectMapRulerToolShown,
  selectTerminalDetailsRulerData,
} from '../../../store';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import appTheme from '../theme/appTheme.json';
import { isNil } from 'lodash';
import RulerLegOverlayContainer from './RulerLegOverlayContainer';
import RulerMarkerOverlayContainer from './RulerMarkerOverlayContainer';

export const LEG_START_INDEX = 0;
export const LEG_END_INDEX = 1;
export const LEG_ID_KEY = 'legId';

/**
 * Google Maps wraps all polylines in a div with a zIndex of 30.
 * In other words, all the polylines will have a zIndex of 30 relative to all other map elements within overlayLayer map pane
 * (i.e. TerminalIconMarker when UP Ruler is enabled)
 */
export const LEG_WRAPPER_Z_INDEX = 30; // DO NOT CHANGE
export interface RulerPathProps {
  map: google.maps.Map;
}

const RulerPath = (props: RulerPathProps) => {
  const { map } = props;
  const rulerEnabled = useSelectorWithMapId(selectMapRulerToolShown);
  const terminalDetailsRulerData = useSelector(selectTerminalDetailsRulerData, shallowEqual);
  const followAircraftEnabled = useSelectorWithMapId(selectMapRulerToolFollowAircraft, shallowEqual);
  const [mapRulerListener, setMapRulerListener] = useState<google.maps.MapsEventListener | null>(null);
  const markersAndPolylines = useSelectorWithMapId(selectMapRulerToolMarkersAndPolylines, shallowEqual);

  /**
   * Use ref to store local copy of markersAndPolylines since
   * handleMapClickLocation needs the values of the most recent
   * markersAndPolylines (redux) object but also updates it.
   * This is to avoid markersAndPolylines from being a dependency in handleMapClickLocation.
   */
  const markersAndPolylinesRef = useRef(markersAndPolylines);

  const [markerEventActive, setMarkerEventActive] = useState(false);

  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const {
    appendLatLngToMapRulerTool,
    prependLatLngToMapRulerTool,
    updateLatLngInMapRulerTool,
    removeLatLngInMapRulerTool,
    setMarkersAndPolylinesMapRulerTool,
  } = mapActionsContext.mapActions;

  const AIRCRAFT_KEY = 'isAircraft';

  const tempMarker = useMemo(() => {
    return new google.maps.Marker({
      opacity: 0.75,
      draggable: false,
      clickable: false,
    });
  }, []);

  const middleWaypointIcon: google.maps.Symbol = useMemo(() => {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: appTheme.modalBackgroundColor, // Inner circle color
      fillOpacity: 1,
      strokeColor: appTheme.mptLightBlue, // Outer circle color
      strokeWeight: 3, // Outer circle width
      scale: 6, // Scale circle to radius of 7.5px
    };
  }, []);

  const outerWaypointIcon: google.maps.Symbol = useMemo(() => {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: appTheme.mptLightBlue, // Inner circle color
      fillOpacity: 1,
      strokeColor: appTheme.mptLightBlue, // Outer circle color
      strokeWeight: 3, // Outer circle width
      scale: 6, // Scale circle to radius of 7.5px
    };
  }, []);

  // Handles creating new markers and polylines as well as updating the path of the previous polyline
  const handleMapClickLocation = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        dispatch(appendLatLngToMapRulerTool(event.latLng));

        /**
         * Get the previous markers and polylines from the ref
         * (to avoid markersAndPolylines from being a dependency,
         * since it is updated in this function)
         */
        const prevMarkersAndPolylines = markersAndPolylinesRef.current;

        const markers = [...prevMarkersAndPolylines.markers];
        const polylines = [...prevMarkersAndPolylines.polylines];

        const index = markers.length; // Get the index of the new point (same as index of new leg)

        const marker = new google.maps.Marker({
          position: event.latLng, // Set the position of the marker to the clicked location
          map: map, // Add the marker to the map
          draggable: true,
          icon: outerWaypointIcon,
          zIndex: index, // Set the zIndex of the marker to the index of the point in markers
        });

        // Set the END position of the previous polyline (if it exists) to the current marker position
        if (!isNil(polylines[index - 1])) {
          polylines[index - 1].getPath().setAt(LEG_END_INDEX, event.latLng as google.maps.LatLng);
        }

        // Create a new polyline and set the START position to the current marker position
        const leg = new google.maps.Polyline({
          strokeColor: appTheme.mptLightBlue,
          strokeWeight: 3,
          geodesic: true,
          strokeOpacity: 1,
          zIndex: index, // For ordering with other polylines, not for other elements in overlayLayer
          map: map,
        });
        leg.set(LEG_ID_KEY, index); // Set the legId key-value pair within the polyline to its index in the array
        leg.getPath().setAt(LEG_START_INDEX, event.latLng as google.maps.LatLng);

        // Append leg to polylines array
        polylines.push(leg);

        // Add mousedown listener to marker to place a temporary marker at its initial position and make the polyline(s) opaque
        marker.addListener('mousedown', () => {
          // Get the zIndex of the marker to determine which polyline(s) to make opaque
          const index = !isNil(marker.getZIndex()) ? (marker.getZIndex() as number) : -1;

          // Set tempMarker options
          tempMarker.setPosition(marker.getPosition());
          tempMarker.setMap(map);
          tempMarker.setIcon(marker.getIcon());

          // Make the polyline(s) that are touching the marker opaque
          if (!isNil(polylines[index - 1])) {
            polylines[index - 1].setOptions({
              strokeOpacity: 0.5,
            });
          }
          polylines[index].setOptions({
            // Leg at index (marker's zIndex) will always exist
            strokeOpacity: 0.5,
          });
        });

        // Add mouseup listener to marker to remove the temporary marker and reset polyline opacity
        marker.addListener('mouseup', () => {
          // Get the zIndex of the marker to determine which polyline(s) to reset
          const index = !isNil(marker.getZIndex()) ? (marker.getZIndex() as number) : -1;

          // Remove temporary marker from map
          tempMarker.setMap(null);
          tempMarker.setPosition(null);
          tempMarker.setIcon(null);

          // Change the polyline opacities back to normal
          if (!isNil(polylines[index - 1])) {
            polylines[index - 1].setOptions({
              strokeOpacity: 1,
            });
          }
          polylines[index].setOptions({
            // Leg at index (marker's zIndex) will always exist
            strokeOpacity: 1,
          });
        });

        // Add dragend listener to marker to update the respective polyline vertex when dragged
        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          if (position) {
            const index = !isNil(marker.getZIndex()) ? (marker.getZIndex() as number) : -1;
            if (index !== -1) {
              dispatch(updateLatLngInMapRulerTool(position, index));

              // Update positions of each polyline vertex that corresponds to the marker
              if (!isNil(polylines[index - 1])) {
                polylines[index - 1].getPath().setAt(LEG_END_INDEX, position);
              }
              polylines[index].getPath().setAt(LEG_START_INDEX, position); // Leg at index (marker's zIndex) will always exist
            }
          }
        });

        marker.addListener('drag', () => {
          google.maps.event.trigger(map, 'mousemove', { latLng: marker.getPosition() });
        });

        // Add mouseover and mouseout listeners to marker to update the markerEventActive state.
        // Prevents any polyline events from triggering (mouseover, mouseout, mousemove, etc.) while
        // the user is hovering over the marker.
        marker.addListener('mouseover', () => {
          setMarkerEventActive(true);
        });
        marker.addListener('mouseout', () => {
          setMarkerEventActive(false);
        });

        markers.push(marker);

        // Update the markers and polylines arrays in redux
        dispatch(setMarkersAndPolylinesMapRulerTool({ markers: markers, polylines: polylines }));
      }
    },
    [
      dispatch,
      appendLatLngToMapRulerTool,
      updateLatLngInMapRulerTool,
      map,
      outerWaypointIcon,
      tempMarker,
      setMarkersAndPolylinesMapRulerTool,
    ],
  );

  // Keeps the local copy of markersAndPolylines in sync with the redux state
  useEffect(() => {
    markersAndPolylinesRef.current = {
      markers: [...markersAndPolylines.markers],
      polylines: [...markersAndPolylines.polylines],
    };
  }, [markersAndPolylines]);

  // Add map click listener when ruler is enabled. Remove listener when ruler is disabled.
  useEffect(() => {
    if (map) {
      if (rulerEnabled) {
        const clickListener = map.addListener('click', handleMapClickLocation);

        setMapRulerListener(clickListener);
      } else {
        if (mapRulerListener) {
          google.maps.event.removeListener(mapRulerListener);

          // Remove all waypoints from the map
          markersAndPolylines.markers.forEach(waypointMarker => {
            waypointMarker.setMap(null);
          });

          // Remove all legs from the map
          markersAndPolylines.polylines.forEach(leg => {
            leg.setMap(null);
          });

          // Clear waypoints and legs arrays in redux
          dispatch(
            setMarkersAndPolylinesMapRulerTool({
              markers: [],
              polylines: [],
            }),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulerEnabled, map, dispatch]);

  // Update the zIndex and icon of each waypoint and leg when the waypoints and legs arrays change
  useEffect(() => {
    // Ensure each waypoint's zIndex and icon is updated when the waypoints array changes
    markersAndPolylines.markers.forEach((marker, index) => {
      // Update the zIndex of each marker to reflect their position in the array
      marker.setZIndex(index);

      if (index === 0 || index === markersAndPolylines.markers.length - 1) {
        // Update the icon of the first and last markers to reflect their position in the array
        marker.setIcon(outerWaypointIcon);
      } else {
        // Update the icon of the middle markers to reflect their position in the array
        marker.setIcon(middleWaypointIcon);
      }
    });

    // Ensure each leg's zIndex is updated when the legs array changes
    markersAndPolylines.polylines.forEach((polyline, index) => {
      // Update the zIndex of each polyline to reflect their position in the array
      polyline.setOptions({ zIndex: index });

      // Set the legId key-value pair within the polyline to its index in the array
      polyline.set(LEG_ID_KEY, index);
    });
  }, [markersAndPolylines, middleWaypointIcon, outerWaypointIcon]);

  // Set and update the aircraft waypoint when the terminal details location changes
  useEffect(() => {
    if (map && rulerEnabled && followAircraftEnabled) {
      // Create shallow copy of waypoints array
      const existingWaypoints = [...markersAndPolylines.markers];

      // Create shallow copy of legs array
      const existingLegs = [...markersAndPolylines.polylines];

      // Check if aircraft marker exists (which will always be first element)
      const aircraftWaypointExists = existingWaypoints[0]?.get(AIRCRAFT_KEY);

      // Get location from ruler data
      const lat = terminalDetailsRulerData?.lat;
      const lng = terminalDetailsRulerData?.lng;

      if (!isNil(lat) && !isNil(lng)) {
        // Create a new LatLng object with the aircraft's location
        const latLng = new google.maps.LatLng(lat, lng);

        if (!aircraftWaypointExists) {
          // Create a new marker for the aircraft's location since one does not exist already
          const aircraftWaypoint = new google.maps.Marker({
            position: latLng, // Set the position of the marker to the selected terminals location
            map: map, // Add the marker to the map
            draggable: false, // Ensures the marker is not draggable, since it will mirror the aircraft location
            icon: outerWaypointIcon,
            zIndex: 0, // Set the zIndex of the marker
          });

          // Set key-value pair within marker to identify it as the aircraft marker (used to remove marker when location is stale)
          // Property of MVCObject, which Marker inherits from (See https://developers.google.com/maps/documentation/javascript/reference/event#MVCObject)
          aircraftWaypoint.set(AIRCRAFT_KEY, true);

          // Add mouseover and mouseout listeners to marker to update the markerEventActive state.
          // Prevents any polyline events from triggering (mouseover, mouseout, mousemove, etc.) while
          // the user is hovering over the marker.
          aircraftWaypoint.addListener('mouseover', () => {
            setMarkerEventActive(true);
          });
          aircraftWaypoint.addListener('mouseout', () => {
            setMarkerEventActive(false);
          });

          // Add the aircraft marker to the beginning of the waypoints array
          existingWaypoints.unshift(aircraftWaypoint);

          // Prepend latLng to LatLng array in redux (used by RulerToolDistanceSummary)
          dispatch(prependLatLngToMapRulerTool(latLng));

          // Create a new polyline for the aircraft's location
          const leg = new google.maps.Polyline({
            strokeColor: appTheme.mptLightBlue,
            strokeWeight: 3,
            geodesic: true,
            strokeOpacity: 1,
            zIndex: 0,
            map: map,
          });

          // Set the START position of the polyline to the aircraft's location
          leg.getPath().setAt(LEG_START_INDEX, latLng);

          // Set the END position of the polyline to the next waypoint's position
          if (existingWaypoints.length >= 2) {
            leg.getPath().setAt(LEG_END_INDEX, existingWaypoints[1].getPosition());
          }

          // Prepend the polyline to the beginning of the legs array
          existingLegs.unshift(leg);
        } else {
          // Update the position of the aircraft marker since it already exists
          existingWaypoints[0].setPosition(latLng);

          // Update first element of LatLng array in redux (used by RulerToolDistanceSummary)
          dispatch(updateLatLngInMapRulerTool(latLng, 0));

          // Update polyline path to reflect aircraft location
          if (!isNil(existingLegs[0])) {
            existingLegs[0].getPath().setAt(LEG_START_INDEX, latLng);
          }
        }

        // Update marker and polyline arrays
        dispatch(
          setMarkersAndPolylinesMapRulerTool({
            markers: existingWaypoints,
            polylines: existingLegs,
          }),
        );
      } else if (isNil(terminalDetailsRulerData) && aircraftWaypointExists) {
        // Remove aircraft marker from map
        existingWaypoints[0].setMap(null);

        // Remove polyline from map
        existingLegs[0].setMap(null);

        // Remove aircraft marker from marker array and first path corresponding to aircraft location
        dispatch(
          setMarkersAndPolylinesMapRulerTool({
            markers: existingWaypoints.slice(1),
            polylines: existingLegs.slice(1),
          }),
        );

        // Remove aircraft marker from redux state
        dispatch(removeLatLngInMapRulerTool(0));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalDetailsRulerData, map, followAircraftEnabled, dispatch]);

  /**
   * Render the hover overlays for each leg.
   * Use markersAndPolylines instead of markersAndPolylinesRef
   * so the lat/lng tooltip can instantly appear right after
   * clicking on the map, creating a new marker.
   */
  return (
    <>
      {map &&
        markersAndPolylines.polylines.map((polyline, index) => {
          return (
            <RulerLegOverlayContainer
              key={index}
              polyline={polyline}
              map={map}
              isMarkerEventActive={markerEventActive}
            />
          );
        })}
      {map &&
        markersAndPolylines.markers.map((marker, index) => {
          return <RulerMarkerOverlayContainer key={index} marker={marker} map={map} />;
        })}
    </>
  );
};

export default React.memo(RulerPath);
